/**
 * CustomersPage.tsx
 * Customer portal with 4 subfolders per customer.
 * Uses currentUser prop (passed from App.tsx) for role-based access.
 *
 * Place at: frontend/src/pages/CustomersPage.tsx
 */
import React, { useState, useRef } from "react";
import { getCached, setCached, invalidate } from "./dataCache";

// ─── Inline SVG icon system (mirrors App.tsx) ─────────────────────────────────
const Icon = ({ d, size = 18, color = "currentColor", style = {} }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    <path d={d} />
  </svg>
);
const Ic = {
  Users:      (p: any) => <Icon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 11a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75" {...p}/>,
  UserPlus:   (p: any) => <Icon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z M19 8v6 M22 11h-6" {...p}/>,
  Plus:       (p: any) => <Icon d="M12 5v14 M5 12h14" {...p}/>,
  Search:     (p: any) => <Icon d="M11 19a8 8 0 100-16 8 8 0 000 16z M21 21l-4.35-4.35" {...p}/>,
  Upload:     (p: any) => <Icon d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12" {...p}/>,
  Download:   (p: any) => <Icon d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3" {...p}/>,
  Eye:        (p: any) => <Icon d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z" {...p}/>,
  Trash:      (p: any) => <Icon d="M3 6h18 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6 M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" {...p}/>,
  File:       (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" {...p}/>,
  FileText:   (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8" {...p}/>,
  BarChart:   (p: any) => <Icon d="M18 20V10 M12 20V4 M6 20v-6" {...p}/>,
  Package:    (p: any) => <Icon d="M16.5 9.4l-9-5.19 M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12" {...p}/>,
  FolderOpen: (p: any) => <Icon d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z M2 10h20" {...p}/>,
  Mail:       (p: any) => <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" {...p}/>,
  Phone:      (p: any) => <Icon d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.4 1.16A2 2 0 012 .82h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" {...p}/>,
  Briefcase:  (p: any) => <Icon d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" {...p}/>,
  User:       (p: any) => <Icon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z" {...p}/>,
  Alert:      (p: any) => <Icon d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" {...p}/>,
  Check:      (p: any) => <Icon d="M22 11.08V12a10 10 0 11-5.93-9.14 M22 4L12 14.01l-3-3" {...p}/>,
  Image:      (p: any) => <Icon d="M21 19a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h4l2 3h8a2 2 0 012 2z" {...p}/>,
  Table:      (p: any) => <Icon d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" {...p}/>,
  FileX:      (p: any) => <Icon d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6 M10 13l4 4 M14 13l-4 4" {...p}/>,
  Edit2:      (p: any) => <Icon d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" {...p}/>,
};

// ─── Theme ────────────────────────────────────────────────────────────────────
const darkTheme = {
  bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332",
  border: "#1E293B", borderLight: "#334155",
  text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B",
  accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.15)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
};
const lightTheme = {
  bg: "#F1F5F9", surface: "#FFFFFF", surfaceHover: "#F8FAFC",
  border: "#E2E8F0", borderLight: "#CBD5E1",
  text: "#1E293B", textMuted: "#475569", textDim: "#94A3B8",
  accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.08)",
  green: "#10B981", yellow: "#F59E0B", red: "#EF4444",
  purple: "#8B5CF6", orange: "#F97316",
};
const getTheme = () => {
  const s = typeof localStorage !== "undefined" ? localStorage.getItem("engportal-theme") : null;
  return s === "light" ? lightTheme : darkTheme;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const FOLDERS = [
  { key: "visit_notes",      label: "Visit Notes",           Icon: Ic.FileText,   color: "#3B82F6" },
  { key: "test_data",        label: "Customer Test Data",    Icon: Ic.BarChart,   color: "#10B981" },
  { key: "sample_shipments", label: "Sample Shipments",      Icon: Ic.Package,    color: "#F97316" },
  { key: "program_docs",     label: "Program Documentation", Icon: Ic.FolderOpen, color: "#8B5CF6" },
];

// ─── Types ────────────────────────────────────────────────────────────────────
interface Customer {
  id: number; name: string; code: string; short_code: string; industry: string;
  status: string;
  contact_name: string; contact_email: string; contact_phone: string;
  notes: string; is_active: boolean;
  file_counts: Record<string, number>;
}
interface CFile {
  id: number; customer: number; folder_type: string; folder_label: string;
  title: string; description: string; file_url: string;
  original_filename: string; file_size: number; mime_type: string;
  visit_date?: string; attendees?: string;
  tracking_number?: string; shipment_date?: string; carrier?: string;
  uploaded_by_name: string; created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtBytes(b: number): string {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}
function fmtDate(s?: string): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}
function getCookie(name: string): string {
  let val = "";
  if (document.cookie) {
    document.cookie.split(";").forEach(c => {
      const t = c.trim();
      if (t.startsWith(name + "=")) val = decodeURIComponent(t.slice(name.length + 1));
    });
  }
  return val;
}
async function api(url: string, opts: RequestInit = {}) {
  const { headers: extraHeaders, ...restOpts } = opts as any;
  const res = await fetch(url, {
    headers: { "X-CSRFToken": getCookie("csrftoken"), ...(extraHeaders || {}) },
    credentials: "include", ...restOpts,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const d = await res.json(); msg = d.error || d.detail || JSON.stringify(d); } catch {}
    throw new Error(msg);
  }
  if (res.status === 204) return null;
  return res.json();
}
function FileTypeIcon({ mime, filename }: { mime: string; filename: string }) {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <Ic.FileText size={16} color="#EF4444" />;
  if (["xlsx","xls","csv"].includes(ext!)) return <Ic.Table size={16} color="#10B981" />;
  if (["docx","doc"].includes(ext!)) return <Ic.FileText size={16} color="#3B82F6" />;
  if (["png","jpg","jpeg","gif","webp"].includes(ext!)) return <Ic.Image size={16} color="#F97316" />;
  return <Ic.File size={16} color="#94A3B8" />;
}
const inputStyle = (theme: any): React.CSSProperties => ({
  width: "100%", padding: "9px 12px", background: theme.bg,
  border: `1px solid ${theme.border}`, borderRadius: 8,
  color: theme.text, fontSize: 13, outline: "none", boxSizing: "border-box",
});

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CustomersPage({ currentUser }: { currentUser?: any }) {
  const theme = getTheme();

  // Match exact same pattern as QualityDocsPage in App.tsx
  const isManager = currentUser?.role === 'engineering_manager' || currentUser?.role === 'executive';
  // Uploading customer documentation is broadly useful and low-risk (it only
  // adds files). Allow any authenticated user except read-only 'viewer'.
  // Deletion stays manager-only (see the Delete gate below) as the safety net.
  const canUpload = !!currentUser && currentUser?.role !== 'viewer';

  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState<Customer | null>(null);
  const [activeFolder, setActiveFolder] = React.useState("visit_notes");
  const [files, setFiles] = React.useState<CFile[]>([]);
  const [filesLoading, setFilesLoading] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [showAddCust, setShowAddCust] = React.useState(false);
  const [showEditCust, setShowEditCust] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);
  const [preview, setPreview] = React.useState<CFile | null>(null);
  const [delConfirm, setDelConfirm] = React.useState<CFile | null>(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => { loadCustomers(); }, [showArchived]);

  async function toggleArchiveCustomer(c: Customer) {
    const verb = (c as any).status === "inactive" ? "unarchive" : "archive";
    try {
      await api(`/api/customers/customers/${c.id}/${verb}/`, { method: "POST" });
      setSelected(null);
      // Archiving moves a customer between the active/archived lists — clear both.
      invalidate("customers:active");
      invalidate("customers:archived");
      loadCustomers({ force: true });
    } catch (e: any) { setError(e.message); }
  }

  async function loadCustomers(opts?: { force?: boolean }) {
    const cacheKey = showArchived ? "customers:archived" : "customers:active";
    // Instant paint from cache unless forcing a refresh.
    if (!opts?.force) {
      const cached = getCached(cacheKey);
      if (cached) { setCustomers(cached); setLoading(false); return; }
    }
    setLoading(true);
    try {
      // The API paginates; follow `next` links to load every customer.
      let url: string | null = "/api/customers/customers/" + (showArchived ? "?show_archived=true" : "");
      const all: any[] = [];
      let guard = 0;
      while (url && guard < 100) {
        const d: any = await api(url);
        if (Array.isArray(d)) { all.push(...d); url = null; }
        else {
          all.push(...(d?.results || []));
          if (d?.next) {
            try { const u = new URL(d.next); url = u.pathname + u.search; }
            catch { url = d.next; }
          } else { url = null; }
        }
        guard++;
      }
      // Normalize CRM shape -> the fields this tab renders. The CRM API returns
      // `code` + `primary_contact_*`; alias them so the render code below
      // (short_code / contact_name / etc.) keeps working unchanged.
      const normalized = all.map((c: any) => ({
        ...c,
        short_code: c.code ?? c.short_code ?? "",
        contact_name: c.primary_contact_name ?? c.contact_name ?? "",
        contact_email: c.primary_contact_email ?? c.contact_email ?? "",
        contact_phone: c.primary_contact_phone ?? c.contact_phone ?? "",
        is_active: c.status ? c.status !== "inactive" : true,
        file_counts: c.file_counts ?? {},
      }));
      normalized.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setCached(cacheKey, normalized);
      setCustomers(normalized);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function loadFiles(custId: number, folder: string) {
    setFilesLoading(true);
    try {
      const d = await api(`/api/customers/customers/${custId}/files/?folder=${folder}`);
      setFiles(Array.isArray(d) ? d : []);
    } catch (e: any) { setError(e.message); }
    finally { setFilesLoading(false); }
  }

  function selectCustomer(c: Customer) {
    setSelected(c); setActiveFolder("visit_notes");
    loadFiles(c.id, "visit_notes"); setShowUpload(false);
  }

  function selectFolder(f: string) {
    setActiveFolder(f);
    if (selected) loadFiles(selected.id, f);
    setShowUpload(false);
  }

  async function handleDelete(f: CFile) {
    try {
      await api(`/api/customers/files/${f.id}/`, { method: "DELETE" });
      setFiles(p => p.filter(x => x.id !== f.id));
      if (selected) {
        const upd = (c: Customer) => ({ ...c, file_counts: { ...c.file_counts, [f.folder_type]: Math.max(0, (c.file_counts[f.folder_type] || 1) - 1), total: Math.max(0, (c.file_counts.total || 1) - 1) } });
        setSelected(p => p ? upd(p) : null);
        setCustomers(p => p.map(c => c.id === selected.id ? upd(c) : c));
        invalidate("customers:active"); invalidate("customers:archived");
      }
      setDelConfirm(null);
    } catch (e: any) { setError(e.message); }
  }

  const filtered = customers.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.short_code || "").toLowerCase().includes(search.toLowerCase())
  );
  const folderMeta = FOLDERS.find(f => f.key === activeFolder)!;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: theme.bg }}>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: `1px solid ${theme.border}` }}>
        <Ic.Users size={20} color={theme.accent} />
        <h2 style={{ margin: 0, color: theme.text, fontSize: 18, fontWeight: 600 }}>Customers</h2>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAddCust(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <Ic.Plus size={14} /> Add Customer
        </button>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, margin: "12px 20px", padding: "10px 14px", color: "#EF4444", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <Ic.Alert size={14} /> {error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Sidebar */}
        <div style={{ width: 260, borderRight: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <div style={{ padding: "12px 12px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "7px 10px" }}>
              <Ic.Search size={14} color={theme.textDim} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers…"
                style={{ background: "none", border: "none", color: theme.text, fontSize: 13, outline: "none", flex: 1 }} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: theme.textMuted, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: 20, color: theme.textDim, fontSize: 13, textAlign: "center" }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 20, color: theme.textDim, fontSize: 13, textAlign: "center" }}>No customers found</div>
            ) : filtered.map(c => {
              const isActive = selected?.id === c.id;
              return (
                <div key={c.id} onClick={() => selectCustomer(c)} style={{
                  padding: "10px 14px", cursor: "pointer",
                  borderLeft: `3px solid ${isActive ? theme.accent : "transparent"}`,
                  background: isActive ? theme.accentGlow : "transparent", transition: "all 0.15s",
                }}
                  onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = theme.surfaceHover; }}
                  onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: theme.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ color: theme.accent, fontSize: 12, fontWeight: 700 }}>
                        {(c.short_code || c.name).slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: isActive ? theme.accent : theme.text, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                      {c.industry && <div style={{ color: theme.textDim, fontSize: 11, marginTop: 1 }}>{c.industry}</div>}
                    </div>
                    {(c.file_counts?.total || 0) > 0 && (
                      <span style={{ background: theme.accent + "33", color: theme.accent, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 10 }}>
                        {c.file_counts.total}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        {!selected ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
            <Ic.Users size={48} color={theme.textDim} />
            <div style={{ color: theme.textMuted, fontSize: 14 }}>Select a customer to view their documents</div>
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

            {/* Customer header */}
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: theme.accent + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ color: theme.accent, fontSize: 15, fontWeight: 700 }}>
                  {(selected.short_code || selected.name).slice(0, 2).toUpperCase()}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: theme.text, fontSize: 16, fontWeight: 700 }}>{selected.name}</div>
                <div style={{ display: "flex", gap: 14, marginTop: 3, flexWrap: "wrap" }}>
                  {selected.industry && <span style={{ color: theme.textMuted, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><Ic.Briefcase size={11} />{selected.industry}</span>}
                  {selected.contact_name && <span style={{ color: theme.textMuted, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><Ic.User size={11} />{selected.contact_name}</span>}
                  {selected.contact_email && (
                    <a href={`mailto:${selected.contact_email}`} style={{ color: theme.accent, fontSize: 12, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                      <Ic.Mail size={11} />{selected.contact_email}
                    </a>
                  )}
                  {selected.contact_phone && <span style={{ color: theme.textMuted, fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}><Ic.Phone size={11} />{selected.contact_phone}</span>}
                </div>
              </div>
              {(isManager || canUpload) && (
                <div style={{ display: "flex", gap: 8 }}>
                  {isManager && (
                    <button onClick={() => toggleArchiveCustomer(selected)} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {(selected as any).status === "inactive" ? "Unarchive" : "Archive"}
                  </button>
                  )}
                  {isManager && (
                    <button onClick={() => setShowEditCust(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <Ic.Edit2 size={13} /> Edit
                  </button>
                  )}
                  {canUpload && (
                    <button onClick={() => setShowUpload(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <Ic.Upload size={13} /> Upload
                  </button>
                  )}
                </div>
              )}
            </div>

            {/* Folder tabs */}
            <div style={{ display: "flex", borderBottom: `1px solid ${theme.border}`, padding: "0 20px", background: theme.surface }}>
              {FOLDERS.map(f => {
                const isActive = activeFolder === f.key;
                const count = selected.file_counts?.[f.key] || 0;
                return (
                  <button key={f.key} onClick={() => selectFolder(f.key)} style={{
                    display: "flex", alignItems: "center", gap: 7, padding: "11px 14px",
                    background: "none", border: "none",
                    borderBottom: `2px solid ${isActive ? f.color : "transparent"}`,
                    color: isActive ? f.color : theme.textMuted,
                    fontSize: 13, fontWeight: isActive ? 600 : 500, cursor: "pointer",
                    transition: "all 0.15s", marginBottom: -1,
                  }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = theme.text; }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.color = theme.textMuted; }}
                  >
                    <f.Icon size={14} color={isActive ? f.color : undefined} />
                    {f.label}
                    {count > 0 && (
                      <span style={{ background: isActive ? f.color + "33" : theme.border, color: isActive ? f.color : theme.textDim, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* File list */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
              {filesLoading ? (
                <div style={{ color: theme.textDim, fontSize: 13, textAlign: "center", paddingTop: 40 }}>Loading…</div>
              ) : files.length === 0 ? (
                <EmptyFolder folder={folderMeta} theme={theme} onUpload={canUpload ? () => setShowUpload(true) : undefined} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {files.map(f => (
                    <FileRow key={f.id} file={f} theme={theme} isManager={isManager}
                      onPreview={() => setPreview(f)} onDelete={() => setDelConfirm(f)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showUpload && selected && (
        <UploadModal customer={selected} defaultFolder={activeFolder} theme={theme}
          onClose={() => setShowUpload(false)}
          onSuccess={(f: CFile) => {
            setFiles(p => [f, ...p]);
            const upd = (c: Customer) => ({ ...c, file_counts: { ...c.file_counts, [f.folder_type]: (c.file_counts[f.folder_type] || 0) + 1, total: (c.file_counts.total || 0) + 1 } });
            setSelected(p => p ? upd(p) : null);
            setCustomers(p => p.map(c => c.id === selected.id ? upd(c) : c));
            invalidate("customers:active"); invalidate("customers:archived");
            if (f.folder_type !== activeFolder) selectFolder(f.folder_type);
            setShowUpload(false);
          }}
        />
      )}

      {showAddCust && (
        <AddCustomerModal theme={theme} onClose={() => setShowAddCust(false)}
          onSuccess={(raw: any) => {
            const c = {
              ...raw,
              short_code: raw.code ?? raw.short_code ?? "",
              contact_name: raw.primary_contact_name ?? raw.contact_name ?? "",
              contact_email: raw.primary_contact_email ?? raw.contact_email ?? "",
              contact_phone: raw.primary_contact_phone ?? raw.contact_phone ?? "",
              is_active: raw.status ? raw.status !== "inactive" : true,
              file_counts: raw.file_counts ?? {},
            };
            setCustomers(p => [...p, c].sort((a, b) => a.name.localeCompare(b.name)));
            invalidate("customers:active"); invalidate("customers:archived");
            setShowAddCust(false); selectCustomer(c);
          }}
        />
      )}

      {showEditCust && selected && (
        <EditCustomerModal theme={theme} customer={selected} onClose={() => setShowEditCust(false)}
          onSuccess={(raw: any) => {
            const c = {
              ...selected,
              ...raw,
              short_code: raw.code ?? raw.short_code ?? "",
              contact_name: raw.primary_contact_name ?? raw.contact_name ?? "",
              contact_email: raw.primary_contact_email ?? raw.contact_email ?? "",
              contact_phone: raw.primary_contact_phone ?? raw.contact_phone ?? "",
              is_active: raw.status ? raw.status !== "inactive" : true,
              file_counts: raw.file_counts ?? selected.file_counts ?? {},
            };
            setCustomers(p => p.map(x => x.id === c.id ? c : x).sort((a, b) => a.name.localeCompare(b.name)));
            setSelected(c);
            setShowEditCust(false);
          }}
        />
      )}

      {preview && <PreviewModal file={preview} theme={theme} onClose={() => setPreview(null)} />}

      {delConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 24, maxWidth: 380, width: "90%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <Ic.Trash size={18} color={theme.red} />
              <span style={{ color: theme.text, fontWeight: 600, fontSize: 15 }}>Delete File</span>
            </div>
            <p style={{ color: theme.textMuted, fontSize: 14, margin: "0 0 20px" }}>
              Delete <strong style={{ color: theme.text }}>{delConfirm.title}</strong>? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDelConfirm(null)} style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={() => handleDelete(delConfirm)} style={{ padding: "8px 18px", background: theme.red, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EmptyFolder ──────────────────────────────────────────────────────────────
function EmptyFolder({ folder, theme, onUpload }: any) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 60, gap: 12 }}>
      <div style={{ width: 52, height: 52, borderRadius: 14, background: folder.color + "22", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <folder.Icon size={26} color={folder.color} />
      </div>
      <div style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>No {folder.label} yet</div>
      <div style={{ color: theme.textDim, fontSize: 13 }}>Files uploaded here will appear in this folder</div>
      {onUpload && (
        <button onClick={onUpload} style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, background: folder.color, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <Ic.Upload size={13} /> Upload First File
        </button>
      )}
    </div>
  );
}

// ─── FileRow ──────────────────────────────────────────────────────────────────
function FileRow({ file, theme, isManager, onPreview, onDelete }: any) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 10, transition: "border-color 0.15s" }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = theme.borderLight}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = theme.border}
    >
      <FileTypeIcon mime={file.mime_type} filename={file.original_filename} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.title}</div>
        <div style={{ display: "flex", gap: 12, marginTop: 2, flexWrap: "wrap" }}>
          {file.description && <span style={{ color: theme.textMuted, fontSize: 12 }}>{file.description}</span>}
          {file.visit_date && <span style={{ color: theme.textDim, fontSize: 11 }}>Visit: {fmtDate(file.visit_date)}</span>}
          {file.attendees && <span style={{ color: theme.textDim, fontSize: 11 }}>👥 {file.attendees}</span>}
          {file.tracking_number && <span style={{ color: theme.textDim, fontSize: 11 }}>📦 {file.carrier ? `${file.carrier}: ` : ""}{file.tracking_number}</span>}
          {file.shipment_date && <span style={{ color: theme.textDim, fontSize: 11 }}>Shipped: {fmtDate(file.shipment_date)}</span>}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 2 }}>
          <span style={{ color: theme.textDim, fontSize: 11 }}>{fmtDate(file.created_at)}</span>
          {file.uploaded_by_name && <span style={{ color: theme.textDim, fontSize: 11 }}>by {file.uploaded_by_name}</span>}
          <span style={{ color: theme.textDim, fontSize: 11 }}>{fmtBytes(file.file_size)}</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <IBtn icon={<Ic.Eye size={14} />} title="Preview" hoverColor="#3B82F6" onClick={onPreview} theme={theme} />
        <a href={`/api/customers/files/${file.id}/download/`} download title="Download"
          style={{ width: 30, height: 30, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, display: "inline-flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>
          <Ic.Download size={14} />
        </a>
        {isManager && <IBtn icon={<Ic.Trash size={14} />} title="Delete" hoverColor="#EF4444" onClick={onDelete} theme={theme} />}
      </div>
    </div>
  );
}

function IBtn({ icon, title, hoverColor, onClick, theme }: any) {
  return (
    <button title={title} onClick={onClick} style={{ width: 30, height: 30, borderRadius: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.textMuted, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = hoverColor; (e.currentTarget as HTMLElement).style.borderColor = hoverColor; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = theme.textMuted; (e.currentTarget as HTMLElement).style.borderColor = theme.border; }}
    >{icon}</button>
  );
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────
function UploadModal({ customer, defaultFolder, theme, onClose, onSuccess }: any) {
  const [folder, setFolder] = useState(defaultFolder);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [visitDate, setVisitDate] = useState("");
  const [attendees, setAttendees] = useState("");
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const [shipDate, setShipDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Action items (optional, visit notes): by default ONE task whose checklist is
  // the action items; optionally split each item into its own task.
  const [showTasks, setShowTasks] = useState(false);
  const [splitTasks, setSplitTasks] = useState(false);
  const [taskUsers, setTaskUsers] = useState<any[]>([]);
  const [taskResult, setTaskResult] = useState("");
  // Single-task fields (default mode)
  const [atTitle, setAtTitle] = useState("");
  const [atOwner, setAtOwner] = useState("");
  const [atViewers, setAtViewers] = useState<string[]>([]);
  const [atDue, setAtDue] = useState("");
  const [atItems, setAtItems] = useState<string[]>([""]);  // action items = checklist
  // Split-mode rows (each becomes its own task): {title, owner, viewer, due}
  const [taskRows, setTaskRows] = useState<any[]>([]);
  React.useEffect(() => {
    api("/api/auth/users/").then((u: any) => {
      const list = Array.isArray(u) ? u : (u?.results || []);
      list.sort((a: any, b: any) => (`${a.first_name||''} ${a.last_name||''}`.trim() || a.username || '')
        .toLowerCase().localeCompare((`${b.first_name||''} ${b.last_name||''}`.trim() || b.username || '').toLowerCase()));
      setTaskUsers(list);
    }).catch(() => {});
  }, []);
  // item helpers (single-task mode)
  const addItem = () => setAtItems(it => [...it, ""]);
  const updItem = (i: number, v: string) => setAtItems(it => it.map((x, idx) => idx === i ? v : x));
  const delItem = (i: number) => setAtItems(it => it.filter((_, idx) => idx !== i));
  // viewer multi-select helper
  const toggleViewer = (id: string) => setAtViewers(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]);
  // split-mode row helpers
  const addTaskRow = () => setTaskRows(r => [...r, { title: "", owner: "", viewer: "", due: "" }]);
  const updTaskRow = (i: number, k: string, v: string) => setTaskRows(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const delTaskRow = (i: number) => setTaskRows(r => r.filter((_, idx) => idx !== i));
  const fileRef = useRef<HTMLInputElement>(null);
  const fm = FOLDERS.find(f => f.key === folder)!;

  async function submit() {
    if (!file) { setErr("Please select a file"); return; }
    if (!title.trim()) { setErr("Please enter a title"); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("folder_type", folder);
      fd.append("title", title.trim()); fd.append("description", desc);
      if (folder === "visit_notes") { fd.append("visit_date", visitDate); fd.append("attendees", attendees); }
      if (folder === "sample_shipments") { fd.append("tracking_number", tracking); fd.append("carrier", carrier); fd.append("shipment_date", shipDate); }
      const result = await api(`/api/customers/customers/${customer.id}/upload/`, { method: "POST", body: fd });

      // Create action-item tasks (visit notes only). Upload already succeeded;
      // task failures are surfaced but never undo the upload.
      if (folder === "visit_notes" && showTasks) {
        const ctx = `[From visit: ${customer.name} — ${title.trim() || "visit notes"}]`;
        if (!splitTasks) {
          // DEFAULT: one task whose checklist is the action items.
          const items = atItems.map(s => (s || "").trim()).filter(Boolean);
          if (atOwner && (atTitle.trim() || items.length)) {
            try {
              const payload: any = {
                title: (atTitle.trim() || `Action Items from ${customer.name}${visitDate ? ` visit ${visitDate}` : " visit"}`).slice(0, 255),
                description: ctx,
                task_type: "other", status: "todo", effort_points: 1,
                assigned_to: atOwner,
                flagged_users: atViewers.map(Number),
              };
              if (atDue) payload.due_date = atDue;
              const created = await api("/api/sprints/tasks/", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              });
              // Attach the action items as checklist items on the new task.
              if (created && created.id) {
                for (let i = 0; i < items.length; i++) {
                  try {
                    await api("/api/sprints/checklist-items/", {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ task: created.id, text: items[i], order: i }),
                    });
                  } catch (e) { /* best-effort; task exists */ }
                }
              }
            } catch (e) {
              setTaskResult("Uploaded, but the action-items task failed to create. The upload is saved — you can create the task manually.");
              setBusy(false); return;
            }
          }
        } else {
          // SPLIT: each row is its own task.
          const rows = taskRows.filter(r => r.title.trim() && r.owner);
          let ok = 0; const failed: string[] = [];
          for (const r of rows) {
            try {
              const payload: any = {
                title: r.title.trim().slice(0, 255), description: ctx,
                task_type: "other", status: "todo", effort_points: 1, assigned_to: r.owner,
              };
              if (r.due) payload.due_date = r.due;
              if (r.viewer) payload.flagged_users = [Number(r.viewer)];
              await api("/api/sprints/tasks/", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              });
              ok++;
            } catch (e) { failed.push(r.title.trim()); }
          }
          if (failed.length) {
            setTaskResult(`Uploaded. Created ${ok} task(s); ${failed.length} failed: ${failed.join(", ")}. Upload saved — retry the failed tasks.`);
            setTaskRows(taskRows.filter(r => failed.includes(r.title.trim())));
            setBusy(false); return;
          }
        }
      }
      onSuccess(result);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const is = inputStyle(theme);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 520, width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.Upload size={17} color={theme.accent} />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>Upload to {customer.name}</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>{err}</div>}
        {taskResult && <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid #F59E0B", borderRadius: 8, padding: "9px 12px", color: "#F59E0B", fontSize: 12, marginBottom: 14 }}>{taskResult}</div>}

        {/* Folder selector */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
            Destination Folder <span style={{ color: theme.red }}>*</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {FOLDERS.map(f => (
              <button key={f.key} onClick={() => setFolder(f.key)} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                background: folder === f.key ? f.color + "22" : theme.bg,
                border: `2px solid ${folder === f.key ? f.color : theme.border}`,
                borderRadius: 8, color: folder === f.key ? f.color : theme.textMuted,
                fontSize: 12, fontWeight: folder === f.key ? 600 : 500, cursor: "pointer",
              }}>
                <f.Icon size={13} color={folder === f.key ? f.color : undefined} />
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Title <span style={{ color: theme.red }}>*</span></label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Q1 Visit Notes" style={is} />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Description</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} placeholder="Optional" style={{ ...is, resize: "vertical" as const }} />
        </div>

        {/* Visit Notes extras */}
        {folder === "visit_notes" && <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Visit Date</label>
            <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)} style={is} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Attendees</label>
            <input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="e.g. John Smith, Jane Doe" style={is} />
          </div>
          {/* Action Items — optionally create work task(s) from this meeting */}
          <div style={{ marginBottom: 14, border: `1px solid ${theme.border}`, borderRadius: 8, padding: 10 }}>
            <div onClick={() => { setShowTasks(s => { const ns = !s; if (ns && !atTitle) { const d = visitDate ? ` visit ${visitDate}` : " visit"; setAtTitle(`Action Items from ${customer.name}${d}`); } return ns; }); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
              <span style={{ color: theme.textMuted, fontSize: 11, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
                ✓ Action Items
              </span>
              <span style={{ color: theme.textDim, fontSize: 14 }}>{showTasks ? "▾" : "▸"}</span>
            </div>
            {showTasks && (
              <div style={{ marginTop: 10 }}>
                {/* Mode toggle */}
                <label style={{ display: "flex", alignItems: "center", gap: 6, color: theme.textDim, fontSize: 11, marginBottom: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={splitTasks} onChange={e => { setSplitTasks(e.target.checked); if (e.target.checked && taskRows.length === 0) addTaskRow(); }} />
                  Create each item as a separate task (default: one task with a checklist)
                </label>

                {!splitTasks ? (
                  /* DEFAULT: one task, action items = checklist */
                  <div>
                    <input value={atTitle} onChange={e => setAtTitle(e.target.value)} placeholder="Task title" style={{ ...is, fontSize: 12, marginBottom: 6 }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                      <select value={atOwner} onChange={e => setAtOwner(e.target.value)} style={{ ...is, fontSize: 12, appearance: "auto" as const }}>
                        <option value="">Owner…</option>
                        {taskUsers.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                      </select>
                      <input type="date" value={atDue} onChange={e => setAtDue(e.target.value)} style={{ ...is, fontSize: 12 }} title="Due date (optional)" />
                    </div>
                    {/* Viewers multi-select as chips */}
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: theme.textDim, fontSize: 10, marginBottom: 4 }}>Viewers (optional — click to toggle):</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 66, overflowY: "auto" as const }}>
                        {taskUsers.map((u: any) => {
                          const on = atViewers.includes(String(u.id));
                          return (
                            <span key={u.id} onClick={() => toggleViewer(String(u.id))}
                              style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, cursor: "pointer",
                                background: on ? theme.accent + "22" : theme.bg, color: on ? theme.accent : theme.textDim,
                                border: `1px solid ${on ? theme.accent : theme.border}`, fontWeight: on ? 600 : 400 }}>
                              {u.first_name} {u.last_name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    {/* Action items -> checklist */}
                    <div style={{ color: theme.textDim, fontSize: 10, marginBottom: 4 }}>Action items (become the task's checklist):</div>
                    {atItems.map((it, i) => (
                      <div key={i} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center" }}>
                        <span style={{ color: theme.textDim, fontSize: 11 }}>☐</span>
                        <input value={it} onChange={e => updItem(i, e.target.value)} placeholder={`Item ${i + 1}`} style={{ ...is, fontSize: 12, flex: 1 }} />
                        <button onClick={() => delItem(i)} style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", fontSize: 15 }} title="Remove">×</button>
                      </div>
                    ))}
                    <button onClick={addItem} style={{ background: "none", border: `1px dashed ${theme.border}`, borderRadius: 6, color: theme.accent, fontSize: 12, fontWeight: 600, padding: "6px 12px", cursor: "pointer", width: "100%", marginTop: 4 }}>+ Add item</button>
                    <div style={{ color: theme.textDim, fontSize: 10, marginTop: 6 }}>Creates one task assigned to the owner (they're notified), with these items as its checklist.</div>
                  </div>
                ) : (
                  /* SPLIT: each row its own task */
                  <div>
                    {taskRows.map((row, i) => (
                      <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6, marginBottom: 8, paddingBottom: 8, borderBottom: i < taskRows.length - 1 ? `1px solid ${theme.border}` : "none" }}>
                        <input value={row.title} onChange={e => updTaskRow(i, "title", e.target.value)} placeholder="Action item…" style={{ ...is, fontSize: 12 }} />
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6, alignItems: "center" }}>
                          <select value={row.owner} onChange={e => updTaskRow(i, "owner", e.target.value)} style={{ ...is, fontSize: 12, appearance: "auto" as const }}>
                            <option value="">Owner…</option>
                            {taskUsers.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                          </select>
                          <select value={row.viewer} onChange={e => updTaskRow(i, "viewer", e.target.value)} style={{ ...is, fontSize: 12, appearance: "auto" as const }}>
                            <option value="">Viewer (opt)…</option>
                            {taskUsers.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                          </select>
                          <button onClick={() => delTaskRow(i)} style={{ background: "none", border: "none", color: theme.textDim, cursor: "pointer", fontSize: 16, padding: "0 4px" }} title="Remove">×</button>
                        </div>
                        <input type="date" value={row.due} onChange={e => updTaskRow(i, "due", e.target.value)} style={{ ...is, fontSize: 12 }} title="Due date (optional)" />
                      </div>
                    ))}
                    <button onClick={addTaskRow} style={{ background: "none", border: `1px dashed ${theme.border}`, borderRadius: 6, color: theme.accent, fontSize: 12, fontWeight: 600, padding: "6px 12px", cursor: "pointer", width: "100%" }}>+ Add action item</button>
                    <div style={{ color: theme.textDim, fontSize: 10, marginTop: 6 }}>Each becomes its own work task assigned to the owner (they're notified). Rows need a title + owner.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>}

        {/* Sample Shipment extras */}
        {folder === "sample_shipments" && <>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Carrier</label>
            <input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="e.g. FedEx, UPS" style={is} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Tracking Number</label>
            <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Tracking #" style={is} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>Shipment Date</label>
            <input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)} style={is} />
          </div>
        </>}

        {/* File drop zone */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>File <span style={{ color: theme.red }}>*</span></label>
          <div onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${file ? fm.color : theme.border}`, borderRadius: 8, padding: 20, textAlign: "center" as const, cursor: "pointer", background: file ? fm.color + "11" : theme.bg }}
            onDragOver={e => { e.preventDefault(); (e.currentTarget as HTMLElement).style.borderColor = fm.color; }}
            onDragLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = file ? fm.color : theme.border; }}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, "")); } }}
          >
            <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, "")); } }} />
            {file ? (
              <div style={{ color: fm.color, fontSize: 13, fontWeight: 600 }}>
                <Ic.Check size={20} color={fm.color} style={{ display: "block", margin: "0 auto 6px" }} />
                {file.name} ({fmtBytes(file.size)})
              </div>
            ) : (
              <>
                <Ic.Upload size={20} color={theme.textDim} style={{ display: "block", margin: "0 auto 6px" }} />
                <div style={{ color: theme.textDim, fontSize: 13 }}>Click to browse or drag & drop</div>
              </>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{ padding: "9px 18px", background: fm.color, border: "none", borderRadius: 8, color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Uploading…" : "Upload File"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Customer Modal ───────────────────────────────────────────────────────
function AddCustomerModal({ theme, onClose, onSuccess }: any) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [industry, setIndustry] = useState("");
  const [cName, setCName] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const is = inputStyle(theme);

  async function save() {
    if (!name.trim()) { setErr("Company name is required"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api("/api/customers/customers/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), short_code: code, industry, contact_name: cName, contact_email: cEmail, contact_phone: cPhone, notes }),
      });
      onSuccess(r);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const lbl = (text: string, required = false) => (
    <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
      {text}{required && <span style={{ color: theme.red }}> *</span>}
    </label>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 480, width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.UserPlus size={17} color={theme.accent} />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>Add Customer</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>{err}</div>}

        <div style={{ marginBottom: 14 }}>{lbl("Company Name", true)}<input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Lockheed Martin" style={is} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ marginBottom: 14 }}>{lbl("Short Code")}<input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. LMT" style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Industry")}<input value={industry} onChange={e => setIndustry(e.target.value)} placeholder="e.g. Aerospace" style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Contact Name")}<input value={cName} onChange={e => setCName(e.target.value)} placeholder="Primary contact" style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Phone")}<input value={cPhone} onChange={e => setCPhone(e.target.value)} placeholder="(555) 000-0000" style={is} /></div>
        </div>
        <div style={{ marginBottom: 14 }}>{lbl("Contact Email")}<input type="email" value={cEmail} onChange={e => setCEmail(e.target.value)} placeholder="email@company.com" style={is} /></div>
        <div style={{ marginBottom: 14 }}>{lbl("Notes")}<textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes" style={{ ...is, resize: "vertical" as const }} /></div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ padding: "9px 18px", background: theme.accent, border: "none", borderRadius: 8, color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Saving…" : "Add Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit Customer Modal ──────────────────────────────────────────────────────
function EditCustomerModal({ theme, customer, onClose, onSuccess }: any) {
  const [name, setName] = useState(customer.name || "");
  const [code, setCode] = useState(customer.short_code || customer.code || "");
  const [industry, setIndustry] = useState(customer.industry || "");
  const [cName, setCName] = useState(customer.contact_name || "");
  const [cEmail, setCEmail] = useState(customer.contact_email || "");
  const [cPhone, setCPhone] = useState(customer.contact_phone || "");
  const [notes, setNotes] = useState(customer.notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const is = inputStyle(theme);

  async function save() {
    if (!name.trim()) { setErr("Company name is required"); return; }
    setBusy(true); setErr("");
    try {
      const r = await api(`/api/customers/customers/${customer.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), short_code: code, industry, contact_name: cName, contact_email: cEmail, contact_phone: cPhone, notes }),
      });
      onSuccess(r);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  const lbl = (text: string, required = false) => (
    <label style={{ display: "block", color: theme.textMuted, fontSize: 11, fontWeight: 600, marginBottom: 5, textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
      {text}{required && <span style={{ color: theme.red }}> *</span>}
    </label>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 24, maxWidth: 480, width: "95%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Ic.Edit2 size={17} color={theme.accent} />
          <span style={{ color: theme.text, fontSize: 15, fontWeight: 600 }}>Edit Customer</span>
          <button onClick={onClose} style={{ marginLeft: "auto", background: "none", border: "none", color: theme.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {err && <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #EF4444", borderRadius: 8, padding: "9px 12px", color: "#EF4444", fontSize: 13, marginBottom: 14 }}>{err}</div>}

        <div style={{ marginBottom: 14 }}>{lbl("Company Name", true)}<input value={name} onChange={e => setName(e.target.value)} style={is} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ marginBottom: 14 }}>{lbl("Short Code")}<input value={code} onChange={e => setCode(e.target.value)} style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Industry")}<input value={industry} onChange={e => setIndustry(e.target.value)} style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Contact Name")}<input value={cName} onChange={e => setCName(e.target.value)} placeholder="Primary contact" style={is} /></div>
          <div style={{ marginBottom: 14 }}>{lbl("Phone")}<input value={cPhone} onChange={e => setCPhone(e.target.value)} placeholder="(555) 000-0000" style={is} /></div>
        </div>
        <div style={{ marginBottom: 14 }}>{lbl("Contact Email")}<input type="email" value={cEmail} onChange={e => setCEmail(e.target.value)} placeholder="email@company.com" style={is} /></div>
        <div style={{ marginBottom: 14 }}>{lbl("Notes")}<textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes" style={{ ...is, resize: "vertical" as const }} /></div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.textMuted, cursor: "pointer", fontSize: 13 }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ padding: "9px 18px", background: theme.accent, border: "none", borderRadius: 8, color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────
function PreviewModal({ file, theme, onClose }: any) {
  const viewUrl = `/api/customers/files/${file.id}/view/`;
  const downloadUrl = `/api/customers/files/${file.id}/download/`;
  const isImage = file.mime_type?.startsWith("image/");
  const isPdf = file.mime_type === "application/pdf" || (file.original_filename || "").toLowerCase().endsWith(".pdf");

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", zIndex: 1000 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", background: theme.surface, borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
        <FileTypeIcon mime={file.mime_type} filename={file.original_filename} />
        <div>
          <div style={{ color: theme.text, fontSize: 14, fontWeight: 600 }}>{file.title}</div>
          <div style={{ color: theme.textDim, fontSize: 11 }}>{file.original_filename} · {fmtBytes(file.file_size)}</div>
        </div>
        <div style={{ flex: 1 }} />
        {(isPdf || !isImage) && (
          <a href={viewUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, background: theme.surface, border: `1px solid ${theme.border}`, color: theme.text, padding: "7px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: "none", marginRight: 4 }}>
            <Ic.Eye size={13} /> Open in Tab
          </a>
        )}
        <a href={downloadUrl} download
          style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", padding: "7px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>
          <Ic.Download size={13} /> Download
        </a>
        <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textDim, fontSize: 24, cursor: "pointer", marginLeft: 8 }}>×</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", background: "#111", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isImage ? (
          <img src={viewUrl} alt={file.title}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, padding: 40 }}>
            <div style={{ width: 72, height: 72, borderRadius: 16, background: theme.surface, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FileTypeIcon mime={file.mime_type} filename={file.original_filename} />
            </div>
            <div style={{ textAlign: "center" as const }}>
              <div style={{ color: theme.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{file.title}</div>
              <div style={{ color: theme.textDim, fontSize: 13 }}>{file.original_filename} · {fmtBytes(file.file_size)}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <a href={viewUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 6, background: theme.surface, border: `1px solid ${theme.borderLight}`, color: theme.text, padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                <Ic.Eye size={15} /> Open in New Tab
              </a>
              <a href={downloadUrl} download
                style={{ display: "flex", alignItems: "center", gap: 6, background: theme.accent, color: "#fff", padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                <Ic.Download size={15} /> Download
              </a>
            </div>
            <div style={{ color: theme.textDim, fontSize: 12 }}>
              PDFs and documents open in a new browser tab
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
